<template>
  <div class="workOrder">
    <card-m
      :title="title"
      cardWidth="100%"
      cardHeight="100%"
      maxHeight="unset"
      toolWidth="70%"
      toolJustifyontent="flex-start"
      :show-filter="false"
      :show-export="true"
      :show-column-edit="true"
      export-file-name="任务单列表"
      @export="handleExport"
      @edit-column="openColumnEdit"
    >
      <template slot="card-box">
        <card-table
          tableID="workOrderTable"
          :total="totalCounts"
          @sizeChange="handleSizeChanges"
          @currentChange="handleCurrentChanges"
          :tableColumn="effectiveVisibleColumns"
          :tableData="enrichedWorkOrderData"
          style="height: 100%;"
          :showIndex="true"
          :key="tableRenderKey"
          :currentPage="currentPage"
          :loading="loading"
          :showPaging="true"
          :size="pageSize"
          @handleProductionClick="handleProductionClick"
          @handleDesignClick="handleDesignClick"
          :mobileColumns="mobileColumnEditFilter"

          @column-filter-change="onColumnFilterChange"
          :columnFilterValues="columnFilterValues"
          @textEditSave="handleTextEditSave"
          @dateEditSave="handleDateEditSave"
          @column-width-change="onColumnWidthChange"
          @icon-click="handleStatusIconClick"
        >
        </card-table>
      </template>

    </card-m>

    <!-- 列编辑弹窗 -->
    <column-edit-dialog
      :visible.sync="columnEditVisible"
      :columns="tempOrderedColumns"
      :defaultColumns="effectiveDefaultColumnDefs"
      :title="columnEditTitle"
      :dialogWidth="columnEditDialogWidth"
      @confirm="handleColumnSave"
      @cancel="handleColumnCancel"
    />

    <!-- 任务单异常详情弹窗 -->
    <common-dialog-table
      :visible="exceptionDialogVisible"
      :title="'任务单异常详情 - 任务号: ' + currentExceptionTaskNo"
      :tableColumn="exceptionListColumn"
      :tableData="exceptionList"
      @close="closeExceptionDialog"
    />

  </div>
</template>

<script>
import { UpdateDataList, getAbnormalInfo } from '@/api/mainapi.js';
import UserInfoUtils from '@/utils/userInfoUtils.js';
import columnEditMixin from '@/mixins/columnEditMixin';
import ColumnEditDialog from '@/components/ColumnEditDialog.vue';
import CommonDialogTable from '@/components/card/CommonDialogTable.vue';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';
export default {
  name: 'workOrderProgress',
  mixins: [columnEditMixin],
  props: {
    workOrderData: {  // 接收父组件传递的数据
      type: Array,
      default: () => []
    },
    totalCounts: {  // 接收父组件传递的总数
      type: Number,
      default: 0
    },
    loading: {  // 接收父组件传递的加载状态
      type: Boolean,
      default: false
    },
    title: {  // 新增标题props
      type: String,
      default: '任务单主页'  // 默认值
    },
    showQuantityColumn: {  // 是否显示「数量」列：生产主页隐藏，合同信息页显示
      type: Boolean,
      default: true
    },
    showMaterialColumn: {  // 是否显示「物料属性」列：生产主页隐藏，合同信息页显示
      type: Boolean,
      default: true
    },
  },

  components: { ColumnEditDialog, CommonDialogTable },

  computed: {
    baseColumnDefs() {
      // 生产主页隐藏「数量」「物料属性」两列，合同信息页正常显示
      const hiddenProps = [];
      if (!this.showQuantityColumn) hiddenProps.push('数量');
      if (!this.showMaterialColumn) hiddenProps.push('物料属性');
      return hiddenProps.length
        ? this.workOrderColumns.filter(col => !hiddenProps.includes(col.prop))
        : this.workOrderColumns;
    },

    /** ✅ 根据"是否有异常"+"生产进度"映射"数据状态码"和"状态详情"
     *  —— 是否有异常=true → 红色(0)；否则按生产进度映射
     */
    enrichedWorkOrderData() {
      if (!this.workOrderData || !Array.isArray(this.workOrderData)) return [];
      return this.workOrderData.map(item => {
        const isIssue = item['是否有异常'];
        const progress = item['生产进度'] || '';
        let 数据状态码 = '';
        let 状态详情 = progress;

        if (isIssue === 'true') {
          // 有异常 → 红色
          数据状态码 = 0;
          状态详情 = '异常';
        } else if (progress === '下达' || progress === '结案' ) {
          数据状态码 = 2;
        } else if (progress === '完全入库') {
          数据状态码 = 1;
        } else if (progress === '异常') {
          数据状态码 = 0;
        }else {
          数据状态码 = 4;
        }
        return {
          ...item,
          数据状态码,
          '状态详情': 状态详情,
          // 数量列：只取整数部分，接口没返回该字段时直接置空
          '数量': this.formatQuantity(item['数量'])
        };
      });
    }
  },

  data() {
    return {
      pageType: 'workOrderProgress',

      // 任务单表格列定义
      workOrderColumns: [
        // 【修改】：图标列（状态指示器）
        {
          prop: '数据状态码', // 【关键字段1】: 接收状态码（0,1,2,空），控制图标颜色
          label: '状态',
          width: 55,
          filterable: true,
          filterType: 'select',
          filterOptions: [
            { label: '全部', value: '' },
            { label: '有异常', value: 'true' },
            // { label: '无问题', value: 'false' }
          ],
          custom: true,
          column: {
            type: 'icon', // 指定为图标类型
            icon: {
              // 注意：这里不再使用Element UI图标类名，而是自定义样式
              default: 'status-circle' // 自定义的CSS类名，用于渲染实心圆
            },
            tooltipField: '状态详情' // 【关键字段2】: 悬浮提示的内容字段
          }
        },        
        {
          prop: '任务号',
          label: '任务号',
          filterable: true   // ✅ 新增：标识该列支持筛选
        },
        {
          prop: '产品型号',
          label: '产品型号',
          filterable: true   // ✅ 新增：标识该列支持筛选
        },
        {
          prop: '合同号',
          label: '合同号',
          filterable: true   // ✅ 新增：标识该列支持筛选
        },
        // {
        //   prop: 'BOM进度',
        //   label: 'BOM进度',
        // },
        {
          prop: 'BOM进度',
          label: 'BOM进度',
          width: 100,
          custom: true,
          column: { type: 'progress', field: 'BOM进度' }
        },
        // {
        //   prop: '设计人员',
        //   label: '设计人员',
        // },
        // {
        //   prop: '加工图',
        //   label: '加工图',
        // },        
        // {
        //   prop: '小屋图纸',
        //   label: '小屋图纸',
        // },        
        // {
        //   prop: '装配图',
        //   label: '装配图',
        // },
        // {
        //   prop: '防爆电器',
        //   label: '防爆电器',
        // },        
        // {
        //   prop: '机柜到货',
        //   label: '机柜到货',
        // },   
        {
          prop: '生产车间',
          label: '生产车间',
          filterable: true
        },
        {
          prop: '生产进度',
          label: '生产进度',
          minWidth: 50,
          filterable: true
        },
        {
          prop: '下达日期',
          label: '下达日期',
          width: 110,
          filterable: true,
          filterType: 'dateFrom'
        },
        {
          prop: '计划完工日期',
          label: '计划完工日期',
          width: 120,
          filterable: true,
          filterType: 'dateFrom'
        },
        {
          prop: '任务单执行反馈',
          label: '任务单执行反馈',
          // width: 100,
          custom: true,
          column: {
            type: 'textEdit',
            field: '任务单执行反馈',
            maxDisplayLength: 10,
            // editOnClick: true   // ✅ 新增：点击即编辑
          }
        },
 
        {
          prop: '派工日期',
          label: '派工日期',
          custom: true,
          column: {
            type: 'dateEdit',
            field: '派工日期',
            dateFormat: (date) => {
              if (!date) return '';
              if (typeof date === 'string' && date.length > 10) {
                return date.substring(0, 10);
              }
              return date;
            }
          }
        },      
        {
          prop: '领料/投料',
          label: '领料比',
          custom: true,
          column: { type: 'progress', field: '领料/投料' }
        },
        {
          prop: '备注',
          label: '备注',
        },
        {
          prop: '物料属性',
          label: '物料属性',
        },
        {
          prop: '数量',
          label: '数量',
          width: 90,
          filterable: true  // ✅ 支持按数量筛选，值由父组件转成数字后以 qty 传给后端
        },
        {
          prop: '在途数',
          label: '在途数',
        },
        {
          prop: '可用库存',
          label: '可用库存',
        },
        {
          prop: '操作',
          label: '操作',
          width: 80,
          actions: [
            {
              label: '设计',
              fnName: 'handleDesignClick'
            },
            {
              label: '生产',
              fnName: 'handleProductionClick'
            }
          ] 
        },

      ],

      // 手机端可用列 prop 过滤（供 mixin 保存/加载手机端列偏好）
      mobileColumnFilter: [
        '数据状态码',
        '任务号',
        '产品型号',
        'BOM进度',
        '生产进度',
        '操作',
        
      ],
      // 手机端列编辑弹窗中可编辑的全部列（恢复默认时，mobileColumnFilter 中的列打钩，其余取消打钩）
      mobileColumnEditFilter: [
        '数据状态码',
        '任务号',
        '产品型号',
        '合同号',
        'BOM进度',
        '生产车间',
        '生产进度',
        '下达日期',
        '计划完工日期',
        '任务单执行反馈',
        '派工日期',
        '领料/投料',
        '备注',
        '物料属性',
        '数量',
        '在途数',
        '可用库存',
        '操作',
      ],

      // 筛选条件参数
      taskNoSearchKeyword: '', // 任务单筛选关键词
      productModelSearchKeyword: '', // 产品型号筛选关键词
      contractNoSearchKeyword: '', // 合同号筛选关键词
      departmentSearchKeyword: '', // 生产车间筛选关键词
      columnFilterValues: {},
      productionProgressSearchKeyword: '', // 生产进度筛选关键词
      commitDateSearchKeyword: '', // 下达日期筛选
      planFinishDateSearchKeyword: '', // 计划完工日期筛选
      isIssueKeyword: '', // 是否有异常筛选
      qtyKeyword: '', // 数量筛选（后端字段 qty，按字符串传）
      
      // 分页相关参数
      currentPage: 1,
      pageSize: 50,

      // 异常详情弹窗
      exceptionDialogVisible: false,
      exceptionList: [],
      currentExceptionTaskNo: '',
      exceptionListColumn: [
        { prop: '项目编号', label: '项目编号' },
        { prop: '合同编号', label: '合同编号' },
        { prop: '任务号', label: '任务号' },
        { prop: '相关节点人', label: '相关节点人' },
        { prop: '异常类型', label: '异常类型' },
        { prop: '事件详情', label: '事件详情' },
        { prop: '发生时间', label: '发生时间' },
        { prop: '是否需要处理', label: '是否需要处理' },
        { prop: '处理人', label: '处理人' },
        { prop: '处理时间', label: '处理时间' },
        { prop: '解决时间', label: '解决时间' },
      ],
    };
  },

  async mounted() {
    await this.loadColumnPref();
  },

  methods: {
  // ✅ 新的日期保存方法（inline 编辑）
  async handleDateEditSave(data) {
    const userCode = this.getCurrentUserCode();
    if (!userCode) return;

    if (!data.row.任务号) {
      this.$message.warning('任务号不能为空');
      return;
    }

    if (!data.value) {
      this.$message.warning('请选择派单日期');
      return;
    }

    try {
      const msgData = {
        dispatchDate: data.value,
        updateBy: userCode,
        taskNum: data.row.任务号
      };

      const res = await UpdateDataList({
        Type: 'UpdateTaskInfo',
        msg: JSON.stringify(msgData)
      });

      if (res.data.code === 200) {
        this.$message.success('派工日期保存成功');
        
        // ✅ 前端同步
        this.$set(
          this.workOrderData[data.index],
          '派工日期',
          data.value
        );
      } else {
        this.$message.error('保存失败：' + (res.data.message || '未知错误'));
      }
    } catch (error) {
      this.$message.error('网络错误，请检查连接');
    }
  },


  // ✅ 任务单执行反馈保存（inline 编辑）
  async handleTextEditSave(data) {
    const userCode = this.getCurrentUserCode();
    if (!userCode) return;

    if (!data.row.任务号) {
      this.$message.warning('任务号不能为空');
      return;
    }

    try {
      const msgData = {
        taskNum: data.row.任务号,
        updateBy: userCode,
        context: data.value || ''
      };

      const res = await UpdateDataList({
        Type: 'UpdateTaskContent',
        msg: JSON.stringify(msgData)
      });

      if (res.data.code === 200) {
        this.$message.success('反馈内容保存成功');

        // ✅ 前端同步：必须用本行 data.row（与表格行是同一对象引用）。
        // 表格客户端排序后 data.index 是显示位置下标，与 workOrderData 原始顺序不一致，用 index 会改到其他行
        this.$set(data.row, '任务单执行反馈', data.value);
      } else {
        this.$message.error('保存失败：' + (res.data.message || '未知错误'));
      }
    } catch (err) {
      this.$message.error('网络错误，请检查连接');
    }
  },


    // 数量显示格式化：只保留整数部分（去掉小数部分）；空值/非数字一律置空
    formatQuantity(val) {
      if (val === null || val === undefined || val === '') return '';
      const num = Number(val);
      return isNaN(num) ? '' : String(Math.round(num));
    },

    onColumnFilterChange(filterValues) {
      this.taskNoSearchKeyword = filterValues['任务号'] || '';
      this.productModelSearchKeyword = filterValues['产品型号'] || '';
      this.contractNoSearchKeyword = filterValues['合同号'] || '';
      this.departmentSearchKeyword = filterValues['生产车间'] || '';
      this.productionProgressSearchKeyword = filterValues['生产进度'] || '';
      this.commitDateSearchKeyword = filterValues['下达日期'] || '';
      this.planFinishDateSearchKeyword = filterValues['计划完工日期'] || '';
      this.isIssueKeyword = filterValues['数据状态码'] || '';
      this.qtyKeyword = filterValues['数量'] != null ? filterValues['数量'] : '';
      this.columnFilterValues = filterValues;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.emitGetData();
    },

    /** 导出Excel */
    handleExport() {
      const xlsxParam = { raw: true };
      const wb = XLSX.utils.table_to_book(
        document.querySelector('#workOrderTable'),
        xlsxParam
      );
      const wbout = XLSX.write(wb, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'array'
      });
      try {
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          '任务单列表.xlsx'
        );
      } catch (e) {
        if (typeof console !== 'undefined') {
          console.log(e, wbout);
        }
      }
      return wbout;
    },

    
    // 触发获取数据事件
    emitGetData() {
      this.$emit('get-data', {
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        taskNo: this.taskNoSearchKeyword,
        model: this.productModelSearchKeyword,
        contractNo: this.contractNoSearchKeyword,
        department: this.departmentSearchKeyword,
        productionProgress: this.productionProgressSearchKeyword,
        commitDate: this.commitDateSearchKeyword,
        planFinishDate: this.planFinishDateSearchKeyword,
        isIssue: this.isIssueKeyword,
        qty: this.qtyKeyword  // 后端按 String 接收，直接传字符串
      });
    },
    


    // 处理生产按钮链接点击（生产按钮）
    handleProductionClick(data) {
      // data 来自 card-table 的 action 点击
      this.$emit('production-click', data);
    },

    handleDesignClick(data) {
      // data 来自 card-table 的 action 点击
      this.$emit('design-click', data);
    },
          

    
    // 改变每页条数
    handleSizeChanges(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.tableRenderKey++;
      this.emitGetData();
    },
    
    // 改变页码
    handleCurrentChanges(val) {
      this.currentPage = val;
      this.emitGetData();
    },
        // 获取当前用户工号
    getCurrentUserCode() {
      const userCode = UserInfoUtils.getUserCode();
      if (!userCode) {
        this.$message.warning('未获取到用户信息，请重新登录');
        this.$router.push('/login');
        return null;
      }
      return userCode;
    },

    /** 状态图标点击 - 仅异常行弹出异常详情 */
    async handleStatusIconClick({ row }) {
      // 仅当数据状态码为 0（异常）时响应
      if (row['数据状态码'] !== 0 && row['数据状态码'] !== '0') {
        return;
      }
      const taskNo = row['任务号'];
      if (!taskNo) {
        this.$message.warning('该行没有任务号');
        return;
      }

      this.currentExceptionTaskNo = taskNo;

      try {
        const res = await getAbnormalInfo({
          taskNo: taskNo,
          contractNo: '',
          projectNo: ''
        });

        if (res.data.code === 200 && res.data.data && res.data.data.exceptionInfo) {
          const rawList = res.data.data.exceptionInfo;
          this.exceptionList = rawList.map(item => ({
            '项目编号': item['项目编号'] || '',
            '合同编号': item['合同编号'] || '',
            '任务号': item['任务号'] || '',
            '相关节点人': item['相关节点人'] || '',
            '异常类型': item['异常类型'] || '',
            '事件详情': item['事件详情'] || '',
            '发生时间': item['发生时间'] || '',
            '是否需要处理': item['是否需要处理'] || '',
            '处理人': item['处理人'] || '',
            '处理时间': item['处理时间'] || '',
            '解决时间': item['解决时间'] || '',
          }));
          this.exceptionDialogVisible = true;
        } else {
          this.$message.info('该任务单暂无异常详情');
        }
      } catch (error) {
        this.$message.error('查询异常详情失败');
      }
    },

    /** 关闭异常详情弹窗 */
    closeExceptionDialog() {
      this.exceptionDialogVisible = false;
      this.exceptionList = [];
      this.currentExceptionTaskNo = '';
    }
  }
};
</script>

<style scoped lang="scss">
.workOrder {
  display: flex;
  flex-wrap: wrap;
  min-width: 350px;
  position: relative;
  width: 100%;
  // height: calc(100vh); /* 减去标题栏和间距的高度 */
  height: calc(100vh - 65px); /* 减去标题栏和间距的高度 */
  // min-height: 900px;

  flex-direction: column;
}

/* 表格样式调整 */
// .workOrder ::v-deep .card-table .el-table th {
//   padding: 0px 0px 0px 0px !important;
// }

// .workOrder ::v-deep .card-table .el-table td {
//   padding: 2px 0px 2px 0px !important;
// }

// .card-table {
//   ::v-deep .el-table .cell {
//     padding-left: 3px !important;
//     padding-right: 3px !important;
//     padding-top: 1px !important;
//     padding-bottom: 1px !important;
//   }
// }

::v-deep .card-m {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

::v-deep .card-box {
  flex: 1;
  min-height: 0;   /* ⭐ 非常关键 */
  overflow-y: hidden;
  overflow-x: hidden;
}

</style>